
import { useStore } from '@/store'
import * as THREE from 'three'
import { defineComponent, inject, onUnmounted } from 'vue'
import { computed, watch } from 'vue'
import MaterialCreateService from '@/services/Three/material-create.service'
import { futaShapeFragmentShaderString } from '@/shaders/futaShapeFragment'
import { ribbonFragmentShaderString } from '@/shaders/ribbonFragment'
import References from '@/singletons/references'
import frontSettings from '@/settings/front-settings.json'
import TextureLoadService from '@/services/Three/texture-load.service'
import SentrySingleton from '@/singletons/sentry-singleton'

export default defineComponent({
  name: 'ThreeRandselFront',
  setup() {
    const store = useStore()
    const emitter: any = inject('emitter')
    const isBagLoaded = computed(() => store.getters.isBagLoaded)
    const isSceneLoaded = computed(() => store.getters.isSceneLoaded)
    const configFrontId = computed(() => store.getters.configFrontId)
    const configThreadId = computed(() => store.getters.configThreadId)
    const isRainbowConfig = computed(() => store.getters.isRainbowConfig)
    const isRenderer = inject('isRenderer') as boolean
    const materialCreateService: MaterialCreateService = new MaterialCreateService()
    let textureLoadService: TextureLoadService
    let currentTexture = ''
    let currentItoTexture = ''
    let currentNormal = ''

    emitter.on('updateConfig', updateOption)

    // prettier-ignore
    watch(() => isSceneLoaded.value, (_) => {
      try {
        initTextureLoadService()
        let mat = materialCreateService.createMaterial(futaShapeFragmentShaderString, true, true, isRenderer)
        mat.uniforms.itoColor = { value: materialCreateService.createColor(0xFFFFFF) };
        mat.uniforms.removeFutaNoUra = { value: 0 }
        mat.uniforms.futaNoUraOnly = { value: 0 }
        mat.uniforms.futaShapeTexture = { value: null };
        mat.uniforms.futaNoUraTexture = { value: null };
        mat.uniforms.futaShapeItoTexture = { value: null };
        mat.uniforms.normalScale.value = materialCreateService.getNormalMapScale()
        mat.uniforms.roughness.value = materialCreateService.getRoughness()
        mat.uniforms.metalness.value = materialCreateService.getMetalness()
        References.instance.setFrontMaterial(mat)
        setupRibbon()
      }
      catch (err) {
        SentrySingleton.instance.captureException(err)
      }
    }
    )

    function initTextureLoadService() {
      if (!textureLoadService || textureLoadService == undefined) {
        textureLoadService = new TextureLoadService(References.instance.getSceneReferences().maxAnisotropy)
      }
    }

    function setupRibbon() {
      let mat = materialCreateService.createMaterial(ribbonFragmentShaderString, true, true, isRenderer)
      mat.uniforms.itoColor = { value: materialCreateService.createColor(0x000000) };
      mat.uniforms.diffuse.value = materialCreateService.createColor(0xbaaebd)
      mat.uniforms.edgeColor = { value: materialCreateService.createColor(0x00FF00) };
      mat.uniforms.edgeAlpha = { value: 0 };
      mat.uniforms.texture = { value: null };
      mat.uniforms.itoTexture = { value: null };
      mat.uniforms.normalScale.value = materialCreateService.getNormalMapScale()
      mat.uniforms.roughness.value = materialCreateService.getRoughness()
      mat.uniforms.metalness.value = materialCreateService.getMetalness()
      References.instance.setRibbonMaterial(mat)
      textureLoadService.loadImage('default/default_n', (tex: THREE.Texture | null) => {
        References.instance.getRibbonMaterial().uniforms.normalMap.value = tex
      })
    }

    watch(() => isBagLoaded.value, (_) => {
      try {
        References.instance.getBagReferences().fronts.forEach(front => {
          if (frontSettings.hontaiMaterialMeshes.includes(front.name)) {
            front.material = References.instance.getBodyMaterial()
          }
          if (frontSettings.edgeMaterialMeshes.includes(front.name)) {
            front.material = References.instance.getEdgeMaterial()
          }
          if (frontSettings.futaShapeMaterialMeshes.includes(front.name)) {
            front.material = References.instance.getFrontMaterial()
          }
        })
        References.instance.getBagReferences().ribbon.material = References.instance.getRibbonMaterial()
      }
      catch (err) {
        SentrySingleton.instance.captureException(err)
      }
    })
    // prettier-ignore
    watch(() => configFrontId.value, (_) => { updateOption() })

    function updateOption() {
      try {
        if (configFrontId.value) {
          setFront(frontSettings.futaShapeTextures.find((x: any) => x.id == configFrontId.value))
        } else {
          setFront(frontSettings.futaShapeTextures.find((x: any) => x.default && x.default === true))
        }
      } catch (err) {
        SentrySingleton.instance.captureException(err)
      }
    }

    function setFront(obj: any) {
      initTextureLoadService()
      changeVisibilty(obj)
      loadTexture(obj ? obj.texture : null)
      loadItoTexture(obj ? (isRainbowConfig.value ? obj.rainbowTexture : obj.itoTexture) : null)
      loadNormal(obj ? obj.normal : null)
    }

    function changeVisibilty(obj: any) {
      const meshNames = obj && obj.meshes ? obj.meshes : frontSettings.defaultMeshes
      References.instance.getBagReferences().fronts.forEach((front) => {
        front.visible = meshNames.includes(front.name)
      })
    }

    function loadTexture(path: string | null) {
      if (path) {
        if (currentTexture != path) {
          currentTexture = path
          textureLoadService.loadImage(path, (tex: THREE.Texture | null) => {
            References.instance.getFrontMaterial().uniforms.futaShapeTexture = { value: tex }
          })
        }
      } else {
        References.instance.getFrontMaterial().uniforms.futaShapeTexture = { value: null }
      }
    }

    function loadItoTexture(path: string | null) {
      if (path) {
        if (path != currentItoTexture) {
          currentItoTexture = path
          textureLoadService.loadImage(path, (tex: THREE.Texture | null) => {
            References.instance.getFrontMaterial().uniforms.futaShapeItoTexture = { value: tex }
          })
        }
      } else {
        References.instance.getFrontMaterial().uniforms.futaShapeItoTexture = { value: null }
      }
    }

    function loadNormal(path: string | null | undefined) {
      const desiredPath = path ?? 'default/default_n'
      if (desiredPath != currentNormal) {
        currentNormal = desiredPath
        textureLoadService.loadImage(desiredPath, (tex: THREE.Texture | null) => {
          References.instance.getFrontMaterial().uniforms.normalMap.value = tex
        })
      }
    }

    // prettier-ignore
    watch(() => configThreadId.value, (_) => {
      try {
        if (configFrontId.value) {
          setFront(frontSettings.futaShapeTextures.find((x: any) => x.id == configFrontId.value))
        }
      }
      catch (err) {
        SentrySingleton.instance.captureException(err)
      }
    }
    )

    onUnmounted(() => {
      emitter.off('updateConfig', updateOption)
    })

    return {
      isBagLoaded,
      isSceneLoaded,

      configFrontId,
      configThreadId,
      isRainbowConfig
    }
  }
})
